// src/ProjectForm.js
//
// WHAT THIS FILE IS:
// A single form component used for BOTH creating a new Project and
// editing an existing one. This is the standard React pattern - instead
// of writing two nearly-identical forms, one component handles both
// cases depending on what props it receives.
//
// HOW IT DECIDES CREATE vs EDIT:
// If the `project` prop is null/undefined -> we're CREATING (blank form,
// sends a POST request).
// If the `project` prop is an actual project object -> we're EDITING
// (form starts pre-filled with that project's data, sends a PUT request).
//
// PROPS this component receives from its parent (ProjectsPage.js):
//   token      - the auth token, needed for the Authorization header
//   project    - null (create mode) OR an existing project object (edit mode)
//   onSaved    - a function to call after a successful save, so the
//                parent knows to refresh its list and close the form
//   onCancel   - a function to call if the user clicks Cancel

import { useState } from 'react';
import { API_BASE_URL } from './apiConfig';
import { formatApiError } from './formErrors';

function ProjectForm({ token, project, onSaved, onCancel }) {
  // We initialize each field's state from `project` IF it was passed in
  // (edit mode), otherwise fall back to a sensible blank/default value
  // (create mode). The `project?.name` syntax is "optional chaining" -
  // it safely returns undefined instead of crashing if `project` is null,
  // so `project?.name || ''` means "use project.name if it exists,
  // otherwise use an empty string".
  const [name, setName] = useState(project?.name || '');
  const [description, setDescription] = useState(project?.description || '');
  const [startDate, setStartDate] = useState(project?.start_date || '');
  const [endDate, setEndDate] = useState(project?.end_date || '');
  const [status, setStatus] = useState(project?.status || 'planning');

  // error holds a message to show if saving fails; saving tracks whether
  // a request is currently in flight, so we can disable the button and
  // avoid the user accidentally double-submitting.
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); // stop the browser's default full-page-reload form behavior

    setSaving(true);
    setError('');

    // Build the JSON body we'll send - same shape for both create and
    // edit, matching the fields ProjectSerializer expects.
    const payload = {
      name,
      description,
      start_date: startDate,
      end_date: endDate || null,
      // Django's DateField accepts null for "no end date yet" - if the
      // input was left empty, endDate is '', so we convert that to a
      // real null rather than sending an invalid empty date string.
      status,
    };

    // Decide the URL and HTTP method based on whether we're editing an
    // existing project or creating a new one.
    const url = project
      ? `${API_BASE_URL}/api/projects/${project.id}/`  // EDIT: PUT to /api/projects/<id>/
      : `${API_BASE_URL}/api/projects/`;                // CREATE: POST to /api/projects/

    const method = project ? 'PUT' : 'POST';
    // PUT means "replace this existing resource with this data".
    // POST means "create a brand new resource".
    // This is the core REST convention DRF's ModelViewSet follows.

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${token}`,
          // Every write request needs the same auth header as our GET
          // requests - without it, DRF's IsAuthenticated permission
          // would reject this with a 403, same as any other endpoint.
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        // If Django's validation rejects something (e.g. missing
        // required field), it responds with a JSON object describing
        // what's wrong, per field. We surface that instead of a vague
        // generic error, so the user (or you, while learning) can see
        // exactly what Django complained about.
        const errorData = await response.json();
        setError(formatApiError(errorData));
        setSaving(false);
        return;
      }

      // Success - tell the parent component to refresh its list and
      // close this form. We don't handle any of that here; ProjectForm
      // doesn't need to know HOW the parent displays projects, it just
      // reports "I'm done."
      onSaved();
    } catch (err) {
      setError('Could not connect to the server');
      setSaving(false);
    }
  };

  return (
    // A simple card-style form, consistent with the rest of the app's styling
    <div className="bg-white p-6 rounded shadow mb-6 max-w-lg">
      <h2 className="text-lg font-bold mb-4">
        {/* Heading changes depending on mode - another place we branch
            on whether `project` was passed in */}
        {project ? 'Edit Project' : 'New Project'}
      </h2>

      <form onSubmit={handleSubmit}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          // required is HTML's own built-in validation - the browser
          // won't let the form submit at all if this is left empty,
          // giving us basic validation for free before Django even sees it.
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />

        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows="3"
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />

        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />

        <label className="block text-sm font-medium text-gray-700 mb-1">End Date (optional)</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />

        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          {/* These three options match STATUS_CHOICES in your real
              Project model exactly: planning / ongoing / completed */}
          <option value="planning">Planning</option>
          <option value="ongoing">Ongoing</option>
          <option value="completed">Completed</option>
        </select>

        {error && (
          <p className="text-red-500 text-sm mb-4 break-words">{error}</p>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving}
            // disabled while saving prevents a double-click from
            // firing two save requests at once
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>

          <button
            type="button"
            // type="button" is important here - without it, this button
            // would ALSO trigger the form's onSubmit, since browsers
            // treat any button inside a <form> as type="submit" by default
            onClick={onCancel}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default ProjectForm;
